import {
  ANCH_LOCATIONS,
  OPERATIONS_LOCATIONS,
  SEA_LOCATION,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/updateMovement/utils";
import type { VesselCallResponse } from "@services/vesselCall/types";
import type {
  MovementServiceDetailsApi,
  MovementServiceDetailsBackendResponse,
  MovementServiceDocument,
  MovementServiceDocumentApi,
  MovementServiceDocumentGroup,
  MovementServicePreviewData,
  MovementServiceSection,
  MovementServiceSectionField,
  MovementServiceType,
} from "./types";

export type MovementServiceDetailsMapperOptions = {
  showRequestedResourcesOnly?: boolean;
};

type VesselCallDetails = Partial<
  Pick<
    VesselCallResponse,
    | "assignedAnchorageArea"
    | "preferredAnchorageArea"
    | "reasonForAnchoragePreference"
    | "eta"
    | "lastTaskDecision"
  >
>;

const LANG_BASE = "vessel-call.movement-services.preview";

const valueOrDash = (value?: string) => (value != null && value !== "" ? value : "-");

const formatLatitudeLongitude = (
  latitude?: string | number | null,
  longitude?: string | number | null,
) => {
  if (latitude == null || latitude === "" || longitude == null || longitude === "") {
    return "-";
  }

  return `25° ${latitude}' N / 056° ${longitude}' E`;
};

const createField = (
  labelKey: string,
  value?: string,
  options?: { includeWhenUndefined?: boolean },
): MovementServiceSectionField | null => {
  if (value === undefined && !options?.includeWhenUndefined) {
    return null;
  }

  return {
    labelKey,
    value: valueOrDash(value),
  };
};

// Helper function to determine if a location is an anchorage
const isAnchorageLocation = (location: string): boolean => {
  if (!location) return false;
  const upperLocation = location.toUpperCase();
  // Check for anchorage codes or if location contains "ANCHORAGE"
  return (
    upperLocation === "FOAA" ||
    upperLocation === "DOAA" ||
    upperLocation.includes("ANCHORAGE") ||
    upperLocation === "ANCH"
  );
};

const buildMovementSection = (
  api: MovementServiceDetailsApi,
  vesselCallDetails?: VesselCallDetails,
): MovementServiceSection | null => {
  const fields: MovementServiceSectionField[] = [];

  const isSeaToBerth = api.movementType === "SEA_TO_BERTH";
  const isBerthToSea = api.movementType === "BERTH_TO_SEA";
  const isAnchorageToSea = api.movementType === "ANCHORAGE_TO_SEA";
  const isAnchorageToBerth = api.movementType === "ANCHORAGE_TO_BERTH";
  const isSeaToAnchorage = api.movementType === "SEA_TO_ANCHORAGE";
  const isAnchorageToAnchorage = api.movementType === "ANCHORAGE_TO_ANCHORAGE";
  const isBerthToBerth = api.movementType === "BERTH_TO_BERTH";
  const isBerthToAnchorage = api.movementType === "BERTH_TO_ANCHORAGE";

  if (isSeaToAnchorage) {
    const placeholder = {
      labelKey: `${LANG_BASE}.fields.from-area-berth`,
      value: "",
      isPlaceholder: true,
    };

    return {
      id: "movement-info",
      titleKey: `${LANG_BASE}.sections.movement-details`,
      preserveFieldOrder: true,
      fields: [
        createField(`${LANG_BASE}.fields.from`, api.movementDetails.from, {
          includeWhenUndefined: true,
        })!,
        createField(`${LANG_BASE}.fields.to`, api.movementDetails.to, {
          includeWhenUndefined: true,
        })!,
        placeholder,
        createField(`${LANG_BASE}.fields.preferred-area`, api.movementDetails.preferredArea, {
          includeWhenUndefined: true,
        })!,
        placeholder,
        createField(
          `${LANG_BASE}.fields.reason-for-anchorage-preference`,
          api.movementDetails.reasonForAnchoragePreference,
          { includeWhenUndefined: true },
        )!,
        placeholder,
        createField(
          `${LANG_BASE}.fields.assigned-anchorage-area`,
          api.movementDetails.designatedArea ??
            vesselCallDetails?.assignedAnchorageArea?.anchorageAreaCode,
          {
            includeWhenUndefined: true,
          },
        )!,
        placeholder,
        createField(`${LANG_BASE}.fields.location`, api.movementDetails.location, {
          includeWhenUndefined: true,
        })!,
        placeholder,
        createField(
          `${LANG_BASE}.fields.latitude-longitude`,
          formatLatitudeLongitude(api.movementDetails.latitude, api.movementDetails.longitude),
          { includeWhenUndefined: true },
        )!,
      ],
    };
  }

  if (isAnchorageToAnchorage) {
    const placeholder = {
      labelKey: `${LANG_BASE}.fields.from-area-berth`,
      value: "",
      isPlaceholder: true,
    };

    return {
      id: "movement-info",
      titleKey: `${LANG_BASE}.sections.movement-details`,
      preserveFieldOrder: true,
      fields: [
        createField(`${LANG_BASE}.fields.from`, api.movementDetails.from, {
          includeWhenUndefined: true,
        })!,
        createField(`${LANG_BASE}.fields.to`, api.movementDetails.to, {
          includeWhenUndefined: true,
        })!,
        createField(`${LANG_BASE}.fields.from-location`, api.movementDetails.fromLocation, {
          includeWhenUndefined: true,
        })!,
        createField(`${LANG_BASE}.fields.preferred-area`, api.movementDetails.preferredArea, {
          includeWhenUndefined: true,
        })!,
        createField(
          `${LANG_BASE}.fields.latitude-longitude`,
          formatLatitudeLongitude(
            api.movementDetails.fromLatitude,
            api.movementDetails.fromLongitude,
          ),
          { includeWhenUndefined: true },
        )!,
        createField(
          `${LANG_BASE}.fields.reason-for-anchorage-preference`,
          api.movementDetails.reasonForAnchoragePreference,
          { includeWhenUndefined: true },
        )!,
        placeholder,
        createField(
          `${LANG_BASE}.fields.assigned-anchorage-area`,
          api.movementDetails.designatedArea,
          { includeWhenUndefined: true },
        )!,
        placeholder,
        createField(`${LANG_BASE}.fields.location`, api.movementDetails.location, {
          includeWhenUndefined: true,
        })!,
        placeholder,
        createField(
          `${LANG_BASE}.fields.latitude-longitude`,
          formatLatitudeLongitude(api.movementDetails.latitude, api.movementDetails.longitude),
          { includeWhenUndefined: true },
        )!,
      ],
    };
  }

  if (isBerthToAnchorage) {
    const placeholder = {
      labelKey: `${LANG_BASE}.fields.from-area-berth`,
      value: "",
      isPlaceholder: true,
    };

    return {
      id: "movement-info",
      titleKey: `${LANG_BASE}.sections.movement-details`,
      preserveFieldOrder: true,
      fields: [
        createField(`${LANG_BASE}.fields.from`, api.movementDetails.from, {
          includeWhenUndefined: true,
        })!,
        createField(`${LANG_BASE}.fields.to`, api.movementDetails.to, {
          includeWhenUndefined: true,
        })!,
        createField(`${LANG_BASE}.fields.from-berth`, api.movementDetails.fromAreaBerth, {
          includeWhenUndefined: true,
        })!,
        createField(`${LANG_BASE}.fields.preferred-area`, api.movementDetails.preferredArea, {
          includeWhenUndefined: true,
        })!,
        placeholder,
        createField(
          `${LANG_BASE}.fields.reason-for-anchorage-preference`,
          api.movementDetails.reasonForAnchoragePreference,
          { includeWhenUndefined: true },
        )!,
        placeholder,
        createField(
          `${LANG_BASE}.fields.assigned-anchorage-area`,
          api.movementDetails.designatedArea,
          {
            includeWhenUndefined: true,
          },
        )!,
        placeholder,
        createField(`${LANG_BASE}.fields.location`, api.movementDetails.location, {
          includeWhenUndefined: true,
        })!,
        placeholder,
        createField(
          `${LANG_BASE}.fields.latitude-longitude`,
          `${valueOrDash(api.movementDetails.latitude)} / ${valueOrDash(api.movementDetails.longitude)}`,
          { includeWhenUndefined: true },
        )!,
      ],
    };
  }

  const fromAreaBerthLabel =
    isBerthToSea || isBerthToBerth
      ? `${LANG_BASE}.fields.from-berth`
      : isAnchorageToSea || isAnchorageToBerth
        ? `${LANG_BASE}.fields.from-location`
        : `${LANG_BASE}.fields.from-area-berth`;

  const toAreaBerthLabel =
    isSeaToBerth || isAnchorageToBerth || isBerthToBerth
      ? `${LANG_BASE}.fields.to-berth`
      : `${LANG_BASE}.fields.to-area-berth`;

  // Always show "From" (no berth/anchorage allocation for From)
  const fromField = createField(`${LANG_BASE}.fields.from`, api.movementDetails.from, {
    includeWhenUndefined: true,
  });
  if (fromField) fields.push(fromField);

  // Preserve the grid position when movement is SEA_TO_BERTH.
  // Show "From Area / Berth" when movement not SEA_TO_BERTH.
  if (isSeaToBerth) {
    fields.push({
      labelKey: `${LANG_BASE}.fields.from-area-berth`,
      value: "",
      isPlaceholder: true,
    });
  } else {
    const fromAreaBerthValue =
      fromAreaBerthLabel === `${LANG_BASE}.fields.from-location`
        ? api.movementDetails.fromLocation
        : api.movementDetails.fromAreaBerth;
    const fromAreaBerthField = createField(fromAreaBerthLabel, fromAreaBerthValue, {
      includeWhenUndefined: true,
    });
    if (fromAreaBerthField) fields.push(fromAreaBerthField);
  }

  if (isAnchorageToSea || isAnchorageToBerth) {
    const fromCoordinatesField = createField(
      `${LANG_BASE}.fields.latitude-longitude`,
      formatLatitudeLongitude(api.movementDetails.fromLatitude, api.movementDetails.fromLongitude),
      { includeWhenUndefined: true },
    );
    if (fromCoordinatesField) fields.push({ ...fromCoordinatesField, isFullRow: true });
  }

  // Always show "To"
  const toField = createField(`${LANG_BASE}.fields.to`, api.movementDetails.to, {
    includeWhenUndefined: true,
  });
  if (toField) fields.push(toField);

  if (isBerthToSea || isAnchorageToSea) {
    fields.push({
      labelKey: `${LANG_BASE}.fields.to-area-berth`,
      value: "",
      isPlaceholder: true,
    });
  } else {
    const toAreaBerthField = createField(toAreaBerthLabel, api.movementDetails.toAreaBerth, {
      includeWhenUndefined: true,
    });
    if (toAreaBerthField) fields.push(toAreaBerthField);
  }

  // Always show Terminal if available
  const terminalField = createField(`${LANG_BASE}.fields.terminal`, api.movementDetails.terminal, {
    includeWhenUndefined: true,
  });
  if (terminalField) fields.push(terminalField);

  if (api.movementDetails.to && isAnchorageLocation(api.movementDetails.to)) {
    // Always show latitude and longitude (with "-" if missing)
    const latField = createField(`${LANG_BASE}.fields.latitude`, api.movementDetails.latitude, {
      includeWhenUndefined: true,
    });
    if (latField) fields.push(latField);
    const longField = createField(`${LANG_BASE}.fields.longitude`, api.movementDetails.longitude, {
      includeWhenUndefined: true,
    });
    if (longField) fields.push(longField);
  }

  const filteredFields = fields.filter(Boolean) as MovementServiceSectionField[];

  if (!filteredFields.length) {
    return null;
  }

  return {
    id: "movement-info",
    titleKey: `${LANG_BASE}.sections.movement-details`,
    fields: filteredFields,
  };
};

// Map UpdateMovementService field keys to preview time field translation keys
const FIELD_KEY_TO_TIME_FIELD: Record<
  string,
  [string, keyof MovementServiceDetailsApi["timeEstimates"]]
> = {
  rts: [`${LANG_BASE}.fields.rts`, "rts"],
  rta: [`${LANG_BASE}.fields.rta`, "rta"],
  rtd: [`${LANG_BASE}.fields.rtd`, "rtd"],
  eta: [`${LANG_BASE}.fields.eta`, "eta"],
  etd: [`${LANG_BASE}.fields.etd`, "etd"],
  ets: [`${LANG_BASE}.fields.ets`, "ets"],
  ats: [`${LANG_BASE}.fields.ats`, "ats"],
  etc: [`${LANG_BASE}.fields.etc`, "etc"],
  pob: [`${LANG_BASE}.fields.pilot-onboard`, "pilotOnboard"],
  allFast: [`${LANG_BASE}.fields.all-fast`, "allFast"],
  allLinesClear: [`${LANG_BASE}.fields.all-lines-clear`, "allLinesClear"],
  allClear: [`${LANG_BASE}.fields.all-clear`, "allClear"],
  anchorTime: [`${LANG_BASE}.fields.anchor-time`, "anchorTime"],
  anchorAweigh: [`${LANG_BASE}.fields.anchor-aweigh-time`, "anchorAweighTime"],
  pilotUnderway: [`${LANG_BASE}.fields.pilot-underway`, "pilotUnderway"],
  pilotAway: [`${LANG_BASE}.fields.pilot-away`, "pilotAway"],
  firstLineAshore: [`${LANG_BASE}.fields.first-line-ashore`, "firstLineAshore"],
  pilotDisembark: [`${LANG_BASE}.fields.pilot-disembark`, "pilotDisembark"],
  permissionToSail: [
    `${LANG_BASE}.fields.permission-to-sail`,
    "permissionToSail" as keyof MovementServiceDetailsApi["timeEstimates"],
  ],
};

// Determine which time fields should be shown based on movement type
type VisibleTimeField = [labelKey: string, key: keyof MovementServiceDetailsApi["timeEstimates"]];

const getVisibleTimeFields = (api: MovementServiceDetailsApi): VisibleTimeField[] => {
  if (api.movementType === "SEA_TO_BERTH") {
    return [
      [`${LANG_BASE}.fields.requested-time-service`, "rts"],
      [`${LANG_BASE}.fields.rta-readiness`, "rta"],
      [`${LANG_BASE}.fields.eta-readiness`, "eta"],
      [`${LANG_BASE}.fields.ets-readiness`, "ets"],
      [`${LANG_BASE}.fields.etc-completion`, "etc"],
      [`${LANG_BASE}.fields.pilot-boarding-area`, "pilotBoardingArea"],
      [`${LANG_BASE}.fields.pilot-underway`, "pilotUnderway"],
      [`${LANG_BASE}.fields.pilot-on-board`, "pilotOnboard"],
      [`${LANG_BASE}.fields.first-line-ashore`, "firstLineAshore"],
      [`${LANG_BASE}.fields.all-fast`, "allFast"],
      [`${LANG_BASE}.fields.pilot-disembarked`, "pilotDisembark"],
      [`${LANG_BASE}.fields.actual-time-start`, "ats"],
      [`${LANG_BASE}.fields.actual-time-completion`, "atc"],
    ];
  }

  if (api.movementType === "BERTH_TO_SEA") {
    return [
      [`${LANG_BASE}.fields.requested-time-service`, "rts"],
      [`${LANG_BASE}.fields.rtd-readiness`, "rtd"],
      [`${LANG_BASE}.fields.etd-readiness`, "etd"],
      [`${LANG_BASE}.fields.ets-readiness`, "ets"],
      [`${LANG_BASE}.fields.etc-completion`, "etc"],
      [`${LANG_BASE}.fields.pilot-underway`, "pilotUnderway"],
      [`${LANG_BASE}.fields.pilot-on-board`, "pilotOnboard"],
      [`${LANG_BASE}.fields.all-clear`, "allClear"],
      [`${LANG_BASE}.fields.pilot-disembarked`, "pilotDisembark"],
      [`${LANG_BASE}.fields.actual-time-start`, "ats"],
      [`${LANG_BASE}.fields.actual-time-completion`, "atc"],
    ];
  }
  if (api.movementType === "BERTH_TO_ANCHORAGE") {
    return [
      [`${LANG_BASE}.fields.requested-time-service`, "rts"],
      [`${LANG_BASE}.fields.rtd-readiness`, "rtd"],
      [`${LANG_BASE}.fields.etd-readiness`, "etd"],
      [`${LANG_BASE}.fields.ets-readiness`, "ets"],
      [`${LANG_BASE}.fields.etc-completion`, "etc"],
      [`${LANG_BASE}.fields.pilot-underway`, "pilotUnderway"],
      [`${LANG_BASE}.fields.pilot-on-board`, "pilotOnboard"],
      [`${LANG_BASE}.fields.all-clear`, "allClear"],
      [`${LANG_BASE}.fields.pilot-disembarked`, "pilotDisembark"],
      [`${LANG_BASE}.fields.vessel-anchor-time`, "anchorTime"],
      [`${LANG_BASE}.fields.actual-time-start`, "ats"],
      [`${LANG_BASE}.fields.actual-time-completion`, "atc"],
    ];
  }
  if (api.movementType === "ANCHORAGE_TO_SEA") {
    return [
      [`${LANG_BASE}.fields.requested-time-service`, "rts"],
      [`${LANG_BASE}.fields.etd-readiness`, "etd"],
      [`${LANG_BASE}.fields.ats-permission-to-sail`, "ats"],
      [`${LANG_BASE}.fields.atc-anchor-aweigh-time`, "atc"],
    ];
  }
  if (api.movementType === "SEA_TO_ANCHORAGE") {
    return [
      [`${LANG_BASE}.fields.requested-time-service`, "rts"],
      [`${LANG_BASE}.fields.eta-readiness`, "eta"],
      [`${LANG_BASE}.fields.ats-permission-to-anchor`, "ats"],
      [`${LANG_BASE}.fields.atc-anchor-time`, "atc"],
    ];
  }

  if (api.movementType === "ANCHORAGE_TO_ANCHORAGE") {
    return [
      [`${LANG_BASE}.fields.requested-time-service`, "rts"],
      [`${LANG_BASE}.fields.ets-permission-to-heave-anchor`, "ets"],
      [`${LANG_BASE}.fields.ats-anchor-aweigh-underway`, "ats"],
      [`${LANG_BASE}.fields.atc-anchor-time`, "atc"],
    ];
  }

  if (api.movementType === "ANCHORAGE_TO_BERTH") {
    return [
      [`${LANG_BASE}.fields.requested-time-service`, "rts"],
      [`${LANG_BASE}.fields.rta-readiness`, "rta"],
      [`${LANG_BASE}.fields.eta-readiness`, "eta"],
      [`${LANG_BASE}.fields.ets-readiness`, "ets"],
      [`${LANG_BASE}.fields.etc-completion`, "etc"],
      [`${LANG_BASE}.fields.anchor-aweigh-time`, "anchorAweighTime"],
      [`${LANG_BASE}.fields.pilot-underway`, "pilotUnderway"],
      [`${LANG_BASE}.fields.pilot-on-board`, "pilotOnboard"],
      [`${LANG_BASE}.fields.pilot-boarding-area`, "pilotBoardingArea"],
      [`${LANG_BASE}.fields.first-line-ashore`, "firstLineAshore"],
      [`${LANG_BASE}.fields.all-fast`, "allFast"],
      [`${LANG_BASE}.fields.pilot-disembarked`, "pilotDisembark"],
      [`${LANG_BASE}.fields.actual-time-start`, "ats"],
      [`${LANG_BASE}.fields.actual-time-completion`, "atc"],
    ];
  }

  if (api.movementType === "BERTH_TO_BERTH") {
    return [
      [`${LANG_BASE}.fields.requested-time-service`, "rts"],
      [`${LANG_BASE}.fields.rtd-readiness`, "rtd"],
      [`${LANG_BASE}.fields.etd-readiness`, "etd"],
      [`${LANG_BASE}.fields.rta-readiness`, "rta"],
      [`${LANG_BASE}.fields.eta-readiness`, "eta"],
      [`${LANG_BASE}.fields.ets-readiness`, "ets"],
      [`${LANG_BASE}.fields.etc-completion`, "etc"],
      [`${LANG_BASE}.fields.pilot-underway`, "pilotUnderway"],
      [`${LANG_BASE}.fields.pilot-on-board`, "pilotOnboard"],
      [`${LANG_BASE}.fields.all-clear`, "allClear"],
      [`${LANG_BASE}.fields.first-line-ashore`, "firstLineAshore"],
      [`${LANG_BASE}.fields.all-fast`, "allFast"],
      [`${LANG_BASE}.fields.pilot-disembarked`, "pilotDisembark"],
      [`${LANG_BASE}.fields.actual-time-start`, "ats"],
      [`${LANG_BASE}.fields.actual-time-completion`, "atc"],
    ];
  }

  const from = api.movementDetails.from?.toUpperCase() || "";
  const to = api.movementDetails.to?.toUpperCase() || "";

  const isAnchLocation = ANCH_LOCATIONS.has(to);
  const isSeaLocation = to === SEA_LOCATION;
  const isOperationsLocation = OPERATIONS_LOCATIONS.has(to);
  const isFromAnch = ANCH_LOCATIONS.has(from);
  const isFromSea = from === SEA_LOCATION;
  const isFromBerth = OPERATIONS_LOCATIONS.has(from);
  // Import field arrays (matching UpdateMovementService logic)
  const DEFAULT_FIELDS = ["rts", "rta", "rtd", "ets", "etc", "ats", "pob", "allLinesClear"];
  const ANCH_FIELDS = [
    "rts",
    "rta",
    "rtd",
    "ets",
    "etc",
    "ats",
    "pilotUnderway",
    "pob",
    "allClear",
    "pilotAway",
    "anchorTime",
    "anchorAweigh",
    ...(isFromAnch || isFromBerth ? ["allFast", "allLinesClear"] : []),
  ];
  const OPERATIONS_FIELDS = [
    "rts",
    "rta",
    "rtd",
    "ets",
    "etc",
    "ats",
    "anchorAweigh",
    "pob",
    "pilotUnderway",
    "firstLineAshore",
    "allFast",
    "pilotDisembark",
    "allLinesClear",
    ...(!isFromBerth ? ["allClear"] : []),
  ];
  const SEA_FIELDS = [
    "rts",
    "rta",
    "rtd",
    "eta",
    "etd",
    "ets",
    "etc",
    "ats",
    "permissionToSail",
    "pob",
    "allLinesClear",
    "anchorAweigh",
  ];

  // Select field order based on destination (matching UpdateMovementService)
  let fieldOrder: string[];
  if (isAnchLocation) {
    fieldOrder = ANCH_FIELDS;
  } else if (isOperationsLocation) {
    fieldOrder = OPERATIONS_FIELDS;
  } else if (isSeaLocation) {
    fieldOrder = SEA_FIELDS;
  } else {
    fieldOrder = DEFAULT_FIELDS;
  }

  // Apply filtering logic
  const filteredFieldOrder = fieldOrder.filter((fieldKey) => {
    if (fieldKey === "allClear") {
      if (isAnchLocation || isOperationsLocation) {
        return !isFromAnch && !isFromSea;
      }
    }

    if (fieldKey === "allLinesClear") {
      if (isSeaLocation) {
        return !isFromAnch && !isFromSea;
      }
    }

    if (fieldKey === "anchorAweigh") {
      if (isSeaLocation) {
        return isFromAnch;
      }
      if (isOperationsLocation) {
        return isFromAnch;
      }
      if (isAnchLocation) {
        return isFromAnch;
      }
    }

    return true;
  });

  // Convert field keys to time field entries
  const visibleFields: VisibleTimeField[] = [];

  for (const fieldKey of filteredFieldOrder) {
    const timeField = FIELD_KEY_TO_TIME_FIELD[fieldKey];
    if (timeField) {
      visibleFields.push(timeField);
    }
  }

  // Add fields from Close Movement Service that are not in Update Movement Service
  // ATC is always shown in Close Movement Service
  visibleFields.push([`${LANG_BASE}.fields.atc`, "atc"]);

  // Vessel Anchored Shackles in Water is shown in Close Movement Service for TO ANCH
  if (isAnchLocation) {
    visibleFields.push([`${LANG_BASE}.fields.vessel-anchored-shackles`, "vesselAnchoredShackles"]);
  }

  return visibleFields;
};

const buildTimesSection = (api: MovementServiceDetailsApi): MovementServiceSection | null => {
  // Get only the fields that should be visible for this movement type
  const visibleTimeFields = getVisibleTimeFields(api);

  // Create fields for visible time entries, showing "-" if undefined
  const fields = visibleTimeFields
    .map(([labelKey, key]) => {
      const value = api.timeEstimates[key];
      const displayValue =
        key === "ats" && value?.match(/^\d{4}-\d{2}-\d{2}T/)
          ? (formatDateTime(value) ?? value)
          : value;

      return createField(labelKey, displayValue ?? undefined, { includeWhenUndefined: true });
    })
    .filter(Boolean) as MovementServiceSectionField[];

  //Adds a "blank" field after the "First Line Ashore" field for SEA_TO_BERTH movements
  if (api.movementType === "SEA_TO_BERTH") {
    const firstLineAshoreLabelKey = `${LANG_BASE}.fields.first-line-ashore`;
    const firstLineAshoreIndex = fields.findIndex(
      (field) => field.labelKey === firstLineAshoreLabelKey,
    );
    fields.splice(firstLineAshoreIndex + 1, 0, {
      labelKey: firstLineAshoreLabelKey,
      value: "",
      isPlaceholder: true,
    });
  }

  if (api.movementType === "BERTH_TO_SEA" || api.movementType === "ANCHORAGE_TO_BERTH") {
    const estimatedCompletionLabelKey = `${LANG_BASE}.fields.etc-completion`;
    const estimatedCompletionIndex = fields.findIndex(
      (field) => field.labelKey === estimatedCompletionLabelKey,
    );
    fields.splice(estimatedCompletionIndex + 1, 0, {
      labelKey: estimatedCompletionLabelKey,
      value: "",
      isPlaceholder: true,
    });
  }

  if (api.movementType === "BERTH_TO_ANCHORAGE") {
    const placeholderAfter = (labelKey: string) => {
      const fieldIndex = fields.findIndex((field) => field.labelKey === labelKey);
      fields.splice(fieldIndex + 1, 0, {
        labelKey,
        value: "",
        isPlaceholder: true,
      });
    };

    placeholderAfter(`${LANG_BASE}.fields.etc-completion`);
    placeholderAfter(`${LANG_BASE}.fields.vessel-anchor-time`);
  }

  if (api.movementType === "ANCHORAGE_TO_BERTH") {
    const pilotDisembarkLabelKey = `${LANG_BASE}.fields.pilot-disembarked`;
    const pilotDisembarkIndex = fields.findIndex(
      (field) => field.labelKey === pilotDisembarkLabelKey,
    );
    fields.splice(pilotDisembarkIndex + 1, 0, {
      labelKey: pilotDisembarkLabelKey,
      value: "",
      isPlaceholder: true,
    });
  }

  if (api.movementType === "BERTH_TO_BERTH") {
    fields.splice(1, 0, {
      labelKey: `${LANG_BASE}.fields.requested-time-service`,
      value: "",
      isPlaceholder: true,
    });
  }

  if (!fields.length) {
    return null;
  }

  return {
    id: "times",
    titleKey: `${LANG_BASE}.sections.times-and-estimates`,
    preserveFieldOrder:
      api.movementType === "SEA_TO_BERTH" ||
      api.movementType === "BERTH_TO_SEA" ||
      api.movementType === "BERTH_TO_ANCHORAGE" ||
      api.movementType === "ANCHORAGE_TO_BERTH" ||
      api.movementType === "SEA_TO_ANCHORAGE" ||
      api.movementType === "ANCHORAGE_TO_ANCHORAGE" ||
      api.movementType === "BERTH_TO_BERTH",
    fields,
  };
};

const buildAdditionalInfoSection = (
  api: MovementServiceDetailsApi,
  vesselConditionsMap?: Map<string, string>,
): MovementServiceSection | null => {
  const info = api.additionalInformation;
  // Map vessel condition code to name if available
  const vesselConditionName =
    info.vesselCondition && vesselConditionsMap?.has(info.vesselCondition)
      ? vesselConditionsMap.get(info.vesselCondition)
      : info.vesselCondition;
  const fields = [
    createField(`${LANG_BASE}.fields.vessel-condition`, vesselConditionName),
    createField(`${LANG_BASE}.fields.movement-reason`, info.movementReason),
    createField(`${LANG_BASE}.fields.draft-fore`, info.draftFore),
    createField(`${LANG_BASE}.fields.draft-aft`, info.draftAft),
    createField(`${LANG_BASE}.fields.pilotage-type`, info.pilotageType),
    createField(`${LANG_BASE}.fields.vessel-anchored-shackles`, info.vesselAnchoredShackles),
    createField(`${LANG_BASE}.fields.specify-if-others`, info.specifyIfOthers),
    createField(`${LANG_BASE}.fields.protrusions`, info.protrusions),
    createField(`${LANG_BASE}.fields.oil-stains`, info.oilStains),
    createField(`${LANG_BASE}.fields.additional-resource-type`, info.additionalResourceType),
    createField(`${LANG_BASE}.fields.number-of-resources`, info.additionalResourceCount),
    createField(`${LANG_BASE}.fields.additional-documents`, info.additionalDocuments),
  ].filter(Boolean) as MovementServiceSectionField[];

  if (!fields.length) {
    return null;
  }

  return {
    id: "additional-info",
    titleKey: `${LANG_BASE}.sections.vessel-information`,
    fields,
  };
};

const buildSummaryFields = (
  api: MovementServiceDetailsApi,
  vesselConditionsMap?: Map<string, string>,
): MovementServiceSectionField[] => {
  // Map vessel condition code to name if available
  const vesselConditionName =
    api.summary.vesselCondition && vesselConditionsMap?.has(api.summary.vesselCondition)
      ? vesselConditionsMap.get(api.summary.vesselCondition)
      : api.summary.vesselCondition;
  const entries: Array<[string, string | undefined]> = [
    [`${LANG_BASE}.fields.from`, api.summary.from],
    [`${LANG_BASE}.fields.to`, api.summary.to],
    [`${LANG_BASE}.fields.berth-allocation`, api.summary.berthAllocation],
    [`${LANG_BASE}.fields.vessel-condition`, vesselConditionName],
    [`${LANG_BASE}.fields.movement-reason`, api.summary.movementReason],
  ];

  return entries
    .map(([labelKey, value]) => createField(labelKey, value, { includeWhenUndefined: true }))
    .filter(Boolean) as MovementServiceSectionField[];
};

const buildRemarksSection = (api: MovementServiceDetailsApi): MovementServiceSection | null => {
  const remarks = createField(`${LANG_BASE}.fields.comments`, api.additionalInformation.comments);

  if (!remarks) {
    return null;
  }

  return {
    id: "remarks",
    titleKey: `${LANG_BASE}.fields.comments`,
    fields: [remarks],
  };
};

const mapResourcesToSections = (
  api: MovementServiceDetailsApi,
  resourceTypesMap: Map<string, string> | undefined,
  resourcesMap: Map<string, string> | undefined,
  options?: MovementServiceDetailsMapperOptions,
): MovementServiceSection[] | undefined => {
  if (!api.resources?.length) {
    return undefined;
  }
  return api.resources.map((resource, index) => {
    const resourceTypeName =
      resource.resourceType && resourceTypesMap?.has(resource.resourceType)
        ? resourceTypesMap.get(resource.resourceType)
        : resource.resourceType;
    const resourceName =
      resource.resourceName && resourcesMap?.has(resource.resourceName)
        ? resourcesMap.get(resource.resourceName)
        : resource.resourceName;
    const requestedResourceFields = [
      createField(`${LANG_BASE}.fields.resource-type`, resourceTypeName, {
        includeWhenUndefined: true,
      }),
      createField(`${LANG_BASE}.fields.requested-quantity`, String(resource.quantity), {
        includeWhenUndefined: true,
      }),
    ].filter(Boolean) as MovementServiceSectionField[];
    const marineAssignedResourceFields = [
      ...requestedResourceFields,
      createField(`${LANG_BASE}.fields.resource-name`, resourceName, {
        includeWhenUndefined: true,
      }),
      createField(`${LANG_BASE}.fields.start-time-of-usage`, resource.startTimeOfUsage, {
        includeWhenUndefined: true,
      }),
      createField(`${LANG_BASE}.fields.end-time-of-usage`, resource.endTimeOfUsage, {
        includeWhenUndefined: true,
      }),
      createField(`${LANG_BASE}.fields.connection-time`, resource.connectionTime, {
        includeWhenUndefined: true,
      }),
      createField(`${LANG_BASE}.fields.location`, resource.location, {
        includeWhenUndefined: true,
      }),
    ].filter(Boolean) as MovementServiceSectionField[];

    return {
      id: resource.id,
      titleKey: `${LANG_BASE}.sections.resource`,
      titleParams: { index: String(index + 1) },
      fields: options?.showRequestedResourcesOnly
        ? requestedResourceFields
        : marineAssignedResourceFields,
    };
  });
};

const mapDocument = (doc: MovementServiceDocumentApi): MovementServiceDocument => ({
  id: doc.documentId,
  name: doc.documentName,
  fileName: doc.fileName,
  url: doc.fileUrl,
  fileFormat: doc.fileFormat,
  documentType: doc.documentType,
  specify: doc.specify,
  additionalDocumentName: doc.additionalDocumentName,
  issueDate: doc.issueDate,
  summary: doc.summary,
  expiryDate: doc.expiryDate,
  className: doc.className,
});

const mapDocumentGroups = (
  api: MovementServiceDetailsApi,
): MovementServiceDocumentGroup[] | undefined => {
  const groups: MovementServiceDocumentGroup[] = [];

  if (api.documents?.movement?.length) {
    groups.push({
      id: "documents",
      titleKey: `${LANG_BASE}.sections.documents`,
      documents: api.documents.movement.map(mapDocument),
    });
  }

  if (api.documents?.certificates?.length) {
    groups.push({
      id: "certificates",
      titleKey: `${LANG_BASE}.sections.ship-certificates`,
      documents: api.documents.certificates.map(mapDocument),
    });
  }

  return groups.length ? groups : undefined;
};

const formatMovementTypeLabel = (movementType: MovementServiceType): string => {
  return movementType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const formatDateTime = (dateTime: string | null | undefined): string | undefined => {
  if (!dateTime) return undefined;
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return undefined;
    // Format as DD/MM/YYYY HH:mm
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return undefined;
  }
};

export const mapBackendResponseToApi = (
  backend: MovementServiceDetailsBackendResponse,
  vesselCallDetails?: VesselCallDetails,
): MovementServiceDetailsApi => {
  // Fallback to task.owner
  const claimOwnerName = backend.task?.claimOwnerName || backend.task?.owner || "";

  const task = backend.task
    ? {
        taskId: backend.task.taskId,
        role: backend.task.role || "",
        workflowId: backend.task.workflowId,
        runId: backend.task.runId,
        status: backend.task.status,
        claimOwnerName: claimOwnerName,
      }
    : undefined;

  const isSeaToAnchorage = backend.movementType === "SEA_TO_ANCHORAGE";
  const isBerthToAnchorage = backend.movementType === "BERTH_TO_ANCHORAGE";
  const isAnchorageToAnchorage = backend.movementType === "ANCHORAGE_TO_ANCHORAGE";

  const vesselCallEta = formatDateTime(vesselCallDetails?.eta);
  const vesselCallApprovalTime = vesselCallDetails?.lastTaskDecision?.timestamp;

  return {
    id: backend.id,
    requestNumber: backend.orderId,
    movementType: backend.movementType,
    movementTypeLabel: formatMovementTypeLabel(backend.movementType),
    status: backend.status,
    submittedByRole: backend.submittedByRole,
    task,
    terminalReport: null, // Backend response doesn't include terminalReport
    summary: {
      from: backend.from || "",
      to: backend.to || "",
      berthAllocation: backend.berthAllocation,
      vesselCondition: backend.vesselCondition || undefined,
      movementReason: backend.movementReason || undefined,
    },
    // Movement locations are displayed using descriptions instead of code when available
    movementDetails: {
      from: backend.fromDesc || backend.from || "",
      fromLocation: backend.fromLocationDesc || backend.fromLocation || undefined,
      fromAreaBerth: backend.fromAreaBerthDesc || backend.fromAreaBerth || "",
      berthAllocation: backend.berthAllocation,
      designatedArea: backend.designatedAreaDesc || backend.designatedArea || undefined,
      location:
        isSeaToAnchorage || isBerthToAnchorage || isAnchorageToAnchorage
          ? backend.locationDesc || backend.location || undefined
          : undefined,
      preferredArea:
        isAnchorageToAnchorage || isBerthToAnchorage
          ? backend.preferredAnchorageAreaDesc || backend.preferredAnchorageArea || undefined
          : (vesselCallDetails?.preferredAnchorageArea?.anchorageAreaDescription ??
            vesselCallDetails?.preferredAnchorageArea?.anchorageAreaCode),
      reasonForAnchoragePreference:
        isAnchorageToAnchorage || isBerthToAnchorage
          ? backend.anchoragePreferenceReason || undefined
          : vesselCallDetails?.reasonForAnchoragePreference,
      to: backend.toDesc || backend.to || "",
      toAreaBerth: backend.toAreaBerthDesc || backend.toAreaBerth || "",
      latitude: backend.latitude?.toString() || undefined,
      longitude: backend.longitude?.toString() || undefined,
      fromLatitude: backend.fromLatitude?.toString() || undefined,
      fromLongitude: backend.fromLongitude?.toString() || undefined,
      terminal: backend.terminal || undefined,
    },
    timeEstimates: {
      rts: formatDateTime(backend.rts),
      rta: formatDateTime(backend.rta),
      rtd: formatDateTime(backend.rtd),
      eta: isSeaToAnchorage
        ? (formatDateTime(backend.eta) ?? vesselCallEta)
        : formatDateTime(backend.eta),
      etd: formatDateTime(backend.etd),
      ets: formatDateTime(backend.ets),
      ats: isSeaToAnchorage ? (backend.ats ?? vesselCallApprovalTime) : backend.ats,
      etc: formatDateTime(backend.etc),
      atc: formatDateTime(backend.atc),
      pilotOnboard: formatDateTime(backend.pobTime),
      allFast: formatDateTime(backend.allFastTime),
      allLinesClear: formatDateTime(backend.allLinesClearTime),
      allClear: formatDateTime(backend.allClearTime),
      anchorTime: formatDateTime(backend.anchorTime),
      anchorAweighTime: formatDateTime(backend.anchorAweighTime),
      pilotUnderway: formatDateTime(backend.pilotUnderwayTime),
      pilotAway: formatDateTime(backend.pilotAwayTime),
      firstLineAshore: formatDateTime(backend.firstLineAshoreTime),
      pilotDisembark: formatDateTime(backend.pilotDisembarkTime),
      pilotBoardingArea: backend.pilotStationDesc || backend.pilotStationCode || undefined,
      permissionToSail: formatDateTime(backend.permissionToSailTime),
      vesselAnchoredShackles:
        backend.vesselAnchoredShacklesInWater?.toString() ||
        backend.vesselAnchoredShackles?.toString() ||
        backend.timeEstimates?.vesselAnchoredShackles?.toString() ||
        undefined,
    },
    additionalInformation: {
      vesselCondition: backend.vesselCondition || undefined,
      movementReason: backend.movementReason || undefined,
      draftFore: backend.draftFore?.toString() || undefined,
      draftAft: backend.draftAft?.toString() || undefined,
      pilotageType: backend.pilotageType || undefined,
      specifyIfOthers: backend.specifyIfOthers || undefined,
      protrusions:
        backend.hasProtrusionsOrOverhand === true
          ? "Yes"
          : backend.hasProtrusionsOrOverhand === false
            ? "No"
            : undefined,
      oilStains:
        backend.hasOilStains === true ? "Yes" : backend.hasOilStains === false ? "No" : undefined,
      comments: backend.comments || undefined,
      vesselAnchoredShackles:
        backend.vesselAnchoredShacklesInWater?.toString() ??
        backend.vesselAnchoredShackles?.toString() ??
        backend.timeEstimates?.vesselAnchoredShackles?.toString() ??
        undefined,
    },
    resources: backend.additionalResources?.map((resource) => {
      return {
        id: resource.id,
        resourceType: resource.additionalResourceType,
        quantity: resource.numberOfResources,
        resourceName: resource.resourceName || undefined,
        startTimeOfUsage: formatDateTime(resource.startTimeOfUsage),
        endTimeOfUsage: formatDateTime(resource.endTimeOfUsage),
        connectionTime: formatDateTime(resource.connectionTime),
        location: resource.location || undefined,
      };
    }),
    documents: {
      movement: [
        ...(backend.pilotRequestNotification?.map((doc, index) => ({
          documentId: `pilot-${index}`,
          documentName: `${LANG_BASE}.documents.pilot-request-notification`,
          fileName: doc.originalFileName || doc.fileName || doc.name || "-",
          fileUrl: doc.url,
        })) || []),
        ...(backend.h2s?.map((doc, index) => ({
          documentId: `h2s-${index}`,
          documentName: `${LANG_BASE}.documents.h2s`,
          fileName: doc.originalFileName || doc.fileName || doc.name || "-",
          fileUrl: doc.url,
        })) || []),
        ...(backend.additionalDocuments?.map((doc, index) => ({
          documentId: `additional-${index}`,
          documentName: `${LANG_BASE}.documents.additional-document`,
          fileName: doc.originalFileName || doc.fileName || doc.name || "-",
          fileUrl: doc.url,
          fileFormat: doc.fileformat,
          documentType: doc.type,
          specify: doc.specify,
          additionalDocumentName: doc.name,
          issueDate: doc.issueDate,
          expiryDate: doc.expiryDate || undefined,
          summary: doc.summary,
        })) || []),
      ],
      certificates:
        backend.certificates?.map((cert, index) => ({
          documentId: `certificate-${index}`,
          documentName: cert.name || `${LANG_BASE}.documents.ship-certificate`,
          fileName: cert.fileName || cert.name || "certificate",
          fileUrl: cert.url,
          expiryDate: cert.expiryDate,
          className: cert.className,
        })) || [],
    },
    statusDetails: Array.isArray(backend.lastTaskDecision) ? backend.lastTaskDecision : [],
    berthPlan: backend.berthPlan ?? undefined,
    unberthPlan: backend.unberthPlan ?? undefined,
    cancelReasonStatus: {
      cancelReason: backend?.cancelReason || undefined,
      cancelAttachments: backend.cancelAttachments?.[0]
        ? {
            name: backend.cancelAttachments[0]?.originalFileName || "",
            url: backend.cancelAttachments[0]?.url || "",
          }
        : undefined,
    },
    associatedPortService: backend.associatedPortService
      ? backend.associatedPortService
      : undefined,
  };
};

export const mapMovementServiceDetails = (
  api: MovementServiceDetailsApi,
  vesselConditionsMap?: Map<string, string>,
  resourceTypesMap?: Map<string, string>,
  resourcesMap?: Map<string, string>,
  options?: MovementServiceDetailsMapperOptions,
  vesselCallDetails?: VesselCallDetails,
): MovementServicePreviewData => {
  const sections = [
    buildMovementSection(api, vesselCallDetails),
    buildAdditionalInfoSection(api, vesselConditionsMap),
    buildTimesSection(api),
  ].filter(Boolean) as MovementServiceSection[];

  return {
    id: api.id,
    requestNumber: api.requestNumber,
    movementType: api.movementType,
    movementTypeLabel: api.movementTypeLabel,
    status: api.status,
    timeEstimates: {
      ats: api.timeEstimates.ats,
    },
    submittedByRole: api.submittedByRole,
    terminalReport: api.terminalReport ?? null,
    task: api.task ?? null,
    from: api.summary?.from || api.movementDetails?.from || "",
    to: api.summary?.to || api.movementDetails?.to || "",
    summaryFields: buildSummaryFields(api),
    sections,
    remarksSection: buildRemarksSection(api),
    additionalResources: api.resources ?? [],
    resourceSections: mapResourcesToSections(api, resourceTypesMap, resourcesMap, options),
    documentGroups: mapDocumentGroups(api),
    statusDetails: Array.isArray(api.statusDetails) ? api.statusDetails : [],
    berthPlan: api.berthPlan,
    unberthPlan: api.unberthPlan,
    cancelReasonStatus: api.cancelReasonStatus,
    associatedPortService: api.associatedPortService,
  };
};
